import { DestroyRef, Injectable, inject } from "@angular/core";
import { AuthService } from "@core/services/auth/auth.service";
import { LoggerService } from "@core/logging/logger.service";
import { APP_ROUTES } from "@core/constants/routes";

/**
 * Re-asserts the authenticated state when an authentication page is restored
 * from the browser's back/forward cache (bfcache).
 *
 * Why this exists:
 * The OIDC login flow crosses documents and origins
 * (/login -> gigya /authorize -> /proxy -> /callback -> /home). Angular's
 * `replaceUrl: true` only rewrites same-document history entries, so it cannot
 * remove the entries created by the full page navigation in GigyaService or by
 * the identity provider's own redirects. Pressing Back from /home therefore
 * lands on one of those auth documents.
 *
 * When such a document is restored from bfcache the Angular application is NOT
 * re-bootstrapped, so `redirectGuard` never runs and the stale
 * "Authenticating..." / "Connecting to the server..." screen is rendered even
 * though the session is still valid. A `popstate` listener cannot help here:
 * `popstate` only fires for same-document navigation.
 *
 * `pageshow` with `event.persisted === true` is the only reliable signal for a
 * bfcache restore, which is what this service listens for. `location.replace`
 * is used rather than the Router because the restored document has stale router
 * state, and replacing also drops the auth entry from history.
 */
@Injectable({ providedIn: "root" })
export class AuthPageRestoreService {
  private readonly authService = inject(AuthService);
  private readonly log = inject(LoggerService).createContext(
    "AuthPageRestore"
  );

  /**
   * Registers a bfcache restore handler for the calling authentication page.
   * Automatically unregisters when the calling component is destroyed.
   *
   * @param destroyRef DestroyRef of the host component.
   */
  register(destroyRef: DestroyRef): void {
    const onPageShow = (event: PageTransitionEvent): void => {
      // Only a bfcache restore needs handling. A normal load re-bootstraps
      // Angular, and redirectGuard already covers that path.
      if (!event.persisted) {
        return;
      }
      void this.redirectIfAuthenticated();
    };

    globalThis.addEventListener("pageshow", onPageShow);
    destroyRef.onDestroy(() => {
      globalThis.removeEventListener("pageshow", onPageShow);
    });
  }

  private async redirectIfAuthenticated(): Promise<void> {
    try {
      if (await this.authService.isSessionValid()) {
        // replace() so the restored auth page is removed from history instead
        // of being stacked on top of it.
        globalThis.location.replace(APP_ROUTES.HOME);
      }
    } catch (error) {
      // Fail open: leave the user on the auth page so they can log in again.
      this.log.error("Session check failed on bfcache restore", error);
    }
  }
}
